// src/utils/roFormat.js

import {
  PARTS_STATUS,
  PARTS_STATUS_LABELS,
  RO_LIFECYCLE,
  RO_STATUS,
  RO_STATUS_LABELS,
  TRANSPORT_LABELS,
  TRANSPORT_TYPES,
  WORK_MIX_LABELS,
} from "../constants/roStatus";

import {
  getAttentionFlags,
  getLifecycle,
  isCompletedRO,
  isInProgressRO,
  isWaitingForAssignment,
  isWaitingForParts,
  isWipRO,
  normalizePartsStatus,
  normalizeStatus,
  safeString,
} from "./roModel";

export function formatVehicleLabel(ro) {
  const vehicle = safeString(ro?.vehicle).trim();
  if (vehicle) return vehicle;

  const year = safeString(ro?.year).trim();
  const make = safeString(ro?.make).trim();
  const model = safeString(ro?.model).trim();

  return [year, make, model].filter(Boolean).join(" ") || "—";
}

export function formatCustomerLabel(ro) {
  return safeString(ro?.customerName).trim() || "—";
}

export function formatROLabel(ro) {
  const roNumber = safeString(ro?.roNumber).trim();
  if (!roNumber) return "RO —";
  return `RO #${roNumber}`;
}

export function getLatestNotePreview(ro, maxLength = 120) {
  const notes = safeString(ro?.notes).trim();
  if (!notes) return "";

  const lines = notes
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) return "";

  // Prefer the latest non-stamp-like content line
  for (let i = lines.length - 1; i >= 0; i -= 1) {
    const line = lines[i];
    const looksLikeStamp =
      line.includes("—") &&
      /(?:AM|PM)/i.test(line) &&
      /:/.test(line);

    if (!looksLikeStamp) {
      return line.length > maxLength ? `${line.slice(0, maxLength).trim()}…` : line;
    }
  }

  const fallback = lines[lines.length - 1];
  return fallback.length > maxLength
    ? `${fallback.slice(0, maxLength).trim()}…`
    : fallback;
}

export function getPhotoCount(ro) {
  const explicit = Number(ro?.photoCount);
  if (Number.isFinite(explicit) && explicit >= 0) return explicit;

  const photos = Array.isArray(ro?.photos) ? ro.photos : [];
  return photos.length;
}

export function hasPhotos(ro) {
  return getPhotoCount(ro) > 0;
}

export function getRoPrimaryOperationalLabel(ro) {
  const lifecycle = getLifecycle(ro);
  const status = normalizeStatus(ro?.status);
  const partsStatus = normalizePartsStatus(ro?.partsStatus || ro?.parts_status);

  if (status === RO_STATUS.ARCHIVED) return RO_STATUS_LABELS[RO_STATUS.ARCHIVED];
  if (isCompletedRO(ro)) return RO_STATUS_LABELS[RO_STATUS.COMPLETED];
  if (isWaitingForParts(ro)) return RO_STATUS_LABELS[RO_STATUS.WAITING_ON_PARTS];
  if (isInProgressRO(ro)) return RO_STATUS_LABELS[RO_STATUS.IN_PROGRESS];
  if (isWaitingForAssignment(ro)) return RO_STATUS_LABELS[RO_STATUS.DISPATCH];

  if (status === RO_STATUS.ASSIGNED) return RO_STATUS_LABELS[RO_STATUS.ASSIGNED];
  if (status === RO_STATUS.OPEN && lifecycle === RO_LIFECYCLE.OPEN) {
    return RO_STATUS_LABELS[RO_STATUS.ASSIGNED];
  }

  // fallback based on parts state if useful
  if (partsStatus === PARTS_STATUS.ORDERED) {
    return PARTS_STATUS_LABELS[PARTS_STATUS.ORDERED];
  }
  if (partsStatus === PARTS_STATUS.PULLED) {
    return PARTS_STATUS_LABELS[PARTS_STATUS.PULLED];
  }
  if (partsStatus === PARTS_STATUS.ARRIVED) {
    return PARTS_STATUS_LABELS[PARTS_STATUS.ARRIVED];
  }
  if (partsStatus === PARTS_STATUS.QUOTED) {
    return PARTS_STATUS_LABELS[PARTS_STATUS.QUOTED];
  }

  return RO_STATUS_LABELS[status] || "Open";
}

export function getTransportPill(ro) {
  const raw = safeString(ro?.transportType).toUpperCase();
  if (!raw || raw === TRANSPORT_TYPES.OTHER) return null;
  return {
    type: "transport",
    key: raw,
    label: TRANSPORT_LABELS[raw] || raw,
  };
}

export function getWorkMixPill(ro) {
  const raw = safeString(ro?.workMix).toUpperCase();
  if (!raw) return null;
  return {
    type: "workMix",
    key: raw,
    label: WORK_MIX_LABELS[raw] || raw,
  };
}

export function getOperationalStatusPill(ro) {
  return {
    type: "status",
    key: safeString(ro?.status || "").toUpperCase() || "OPEN",
    label: getRoPrimaryOperationalLabel(ro),
  };
}

/**
 * Max 3 core pills:
 * 1) Transportation (optional)
 * 2) Work Mix (optional)
 * 3) Operational Status (required)
 */
export function getRoCorePills(ro) {
  const pills = [];

  const transport = getTransportPill(ro);
  if (transport) pills.push(transport);

  const workMix = getWorkMixPill(ro);
  if (workMix) pills.push(workMix);

  pills.push(getOperationalStatusPill(ro));

  return pills.slice(0, 3);
}

export function getRoAttentionLabels(ro) {
  const labels = [];
  const flags = getAttentionFlags(ro);

  if (flags.waiterNoTech) labels.push("ATTN: Dispatch");
  if (flags.waitingAssignmentTooLong) labels.push("ATTN: Dispatch");
  if (flags.waitingPartsTooLong) labels.push("ATTN: Parts");

  // future-proof manual flags if they later exist on record
  const manualFlags = Array.isArray(ro?.attentionFlags) ? ro.attentionFlags : [];
  manualFlags.forEach((flag) => {
    const label = safeString(flag?.label || flag).trim();
    if (label) labels.push(label);
  });

  return Array.from(new Set(labels));
}

export function isPromiseRisk(ro) {
  const flags = getAttentionFlags(ro);
  return !!flags.promiseOverdue;
}

export function isAgingRisk(ro) {
  const flags = getAttentionFlags(ro);
  return !!(flags.waitingAssignmentTooLong || flags.waitingPartsTooLong);
}

export function getRoSecondaryIndicators(ro) {
  const indicators = [];

  const attentionLabels = getRoAttentionLabels(ro);
  attentionLabels.forEach((label) => {
    indicators.push({
      type: "attention",
      key: label,
      label,
    });
  });

  const photoCount = getPhotoCount(ro);
  if (photoCount > 0) {
    indicators.push({
      type: "photos",
      key: "photos",
      label: `📷 ${photoCount}`,
      count: photoCount,
    });
  }

  if (isPromiseRisk(ro)) {
    indicators.push({
      type: "risk",
      key: "promise-risk",
      label: "!",
    });
  }

  if (isAgingRisk(ro)) {
    indicators.push({
      type: "aging",
      key: "aging",
      label: "⏱",
    });
  }

  return indicators;
}

export function getRoUrgencyLevel(ro) {
  const secondary = getRoSecondaryIndicators(ro);
  const hasRisk = secondary.some((x) => x.type === "risk");
  const hasAttention = secondary.some((x) => x.type === "attention");
  const hasAging = secondary.some((x) => x.type === "aging");

  if (hasRisk) return "risk";
  if (hasAttention || hasAging) return "attention";
  return "normal";
}

export function shouldHighlightRoNumber(ro) {
  return getRoUrgencyLevel(ro) === "risk";
}

export function formatAssignedTechLabel(ro, getTechName) {
  const assignedTechName = safeString(ro?.assignedTechName).trim();
  if (assignedTechName) return assignedTechName;

  const assignedTechId = safeString(ro?.assignedTechId).trim();
  if (assignedTechId && typeof getTechName === "function") {
    return getTechName(assignedTechId);
  }

  return "—";
}

export function formatAdvisorLabel(ro) {
  return safeString(ro?.advisorName).trim() || "—";
}

export function getRoSummaryLine(ro, getTechName) {
  const customer = formatCustomerLabel(ro);
  const vehicle = formatVehicleLabel(ro);
  const tech = formatAssignedTechLabel(ro, getTechName);

  return {
    customer,
    vehicle,
    tech,
    advisor: formatAdvisorLabel(ro),
  };
}

export function isSearchMatch(ro, query) {
  const q = safeString(query).trim().toLowerCase();
  if (!q) return true;

  const hay = [
    ro?.roNumber,
    ro?.tagNumber,
    ro?.customerName,
    ro?.vehicle,
    ro?.vin,
    ro?.advisorName,
    ro?.assignedTechName,
    ro?.concern,
    ro?.notes,
    ro?.partsStatus,
    ro?.status,
    ro?.lifecycle,
  ]
    .map((x) => safeString(x))
    .join(" | ")
    .toLowerCase();

  return hay.includes(q);
}

export function getRoRowViewModel(ro, opts = {}) {
  const getTechName = opts.getTechName;
  const latestNote = getLatestNotePreview(ro);
  const urgency = getRoUrgencyLevel(ro);

  return {
    id: ro?.id || null,
    roLabel: formatROLabel(ro),
    roNumber: safeString(ro?.roNumber).trim() || "—",
    tagNumber: safeString(ro?.tagNumber).trim(),
    customer: formatCustomerLabel(ro),
    vehicle: formatVehicleLabel(ro),
    advisor: formatAdvisorLabel(ro),
    technician: formatAssignedTechLabel(ro, getTechName),
    corePills: getRoCorePills(ro),
    secondaryIndicators: getRoSecondaryIndicators(ro),
    latestNote,
    urgency,
    highlightRoNumber: shouldHighlightRoNumber(ro),
    photoCount: getPhotoCount(ro),
    isWip: isWipRO(ro),
    isCompleted: isCompletedRO(ro),
  };
}